import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Col, Divider, Input, Row, Typography } from 'antd';
import { TbAt, TbInfoCircle, TbUserCog } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AccountVerificationControl, SmallTooltip } from '@/shared';
import { CompanyDomainAutocomplete, PlatformForm as Form } from '@/shared/components/forms';
import { useUserStore } from '@/stores';
import type { User } from '@/types';

import UserAvatar from './UserAvatar';
import { AccountInformationFormValues, buildAccountInformationUpdate } from './accountInformationUpdate';

const Information: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { selectedUser: user, updateUser } = useUserStore();
    const [form] = Form.useForm<AccountInformationFormValues>();

    const [loading, setLoading] = useState(false);
    const companyName = Form.useWatch('companyName', form);
    const companyWebsite = Form.useWatch('companyWebsite', form);
    const selectedCompanyPreview =
        companyName || companyWebsite ? { domain: companyWebsite ?? '', name: companyName ?? '' } : null;

    useEffect(() => {
        if (!user) {
            return;
        }

        form.setFieldsValue({
            firstName: user.firstName,
            lastName: user.lastName,
            emailAddress: user.emailAddress,
            username: user.username,
            title: user.title,
            bio: user.bio,
            calendarLink: user.calendarLink,
            companyName: user.companyName,
            companyWebsite: user.companyWebsite,
        });
    }, [user, form]);

    const handleFinish = async (values: AccountInformationFormValues) => {
        setLoading(true);

        try {
            const updated = await updateUser(
                buildAccountInformationUpdate(values, form.getFieldValue('companyWebsite')),
            );

            if (!updated) {
                toast.error('Unable to Update Account', {
                    description: 'Your changes were not saved. Please try again.',
                });

                return;
            }

            toast('Account Updated', {
                icon: <TbUserCog size='1rem' />,
                description: `${updated.firstName} ${updated.lastName}`,
            });

            setIsDirty(false);
        } finally {
            setLoading(false);
        }
    };

    const [isDirty, setIsDirty] = useState(false);

    // Whenever form values change, check if any field is different from initial user values
    const handleValuesChange = () => {
        if (!user) {
            return;
        }

        const currentValues = form.getFieldsValue();

        const initialValues = {
            firstName: user.firstName,
            lastName: user.lastName,
            emailAddress: user.emailAddress,
            username: user.username,
            title: user.title,
            bio: user.bio,
            calendarLink: user.calendarLink,
            companyName: user.companyName,
            companyWebsite: user.companyWebsite,
        } satisfies AccountInformationFormValues;

        const dirty = Object.entries(initialValues).some(
            ([key, initialValue]) => currentValues[key as keyof AccountInformationFormValues] !== initialValue,
        );

        setIsDirty(dirty);
    };

    const [, forceUpdate] = useState({});

    const isFormInvalid = useMemo(() => {
        const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);

        const isTouched = form.isFieldsTouched(true);

        return !isTouched || hasErrors;
    }, [form]);

    const normalizeUsername = (value: string) => {
        return value
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, '')
            .replace(/_{2,}/g, '_')
            .replace(/^_+/, '')
            .slice(0, 24);
    };

    return (
        <>
            <Typography.Title id='settings-account-general-account-information' level={4}>
                Account Information
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Manage the profile, contact, and workplace information associated with your account.
            </Typography.Paragraph>
            <Form
                layout='vertical'
                form={form}
                name='accountGeneral'
                onFinish={handleFinish}
                requiredMark={false}
                onValuesChange={() => {
                    handleValuesChange();
                    forceUpdate({});
                }}
            >
                <UserAvatar user={user as User} />
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item
                            name='firstName'
                            style={{ marginBottom: '0.5rem' }}
                            label='First Name'
                            rules={[{ required: true, message: 'First name required' }]}
                        >
                            <Input placeholder='First Name...' />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name='lastName'
                            style={{ marginBottom: '0.5rem' }}
                            label='Last Name'
                            rules={[{ required: true, message: 'Last name required' }]}
                        >
                            <Input placeholder='Last Name...' />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Form.Item
                            name='emailAddress'
                            style={{ marginBottom: '0.5rem' }}
                            label={
                                <>
                                    <small style={{ color: token.colorTextTertiary, marginRight: '0.375rem' }}>
                                        Email Address
                                        <SmallTooltip
                                            title={`Changing your email address will remove your account verification.`}
                                        >
                                            <TbInfoCircle
                                                style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }}
                                            />
                                        </SmallTooltip>
                                    </small>
                                </>
                            }
                            rules={[{ required: true, message: 'Email address required' }]}
                        >
                            <Input placeholder='Email Address...' />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            style={{ marginBottom: '0.5rem' }}
                            label={
                                <>
                                    <small style={{ color: token.colorTextTertiary, marginRight: '0.375rem' }}>
                                        Verify Account
                                        <SmallTooltip
                                            title={
                                                user?.emailConfirmed
                                                    ? `You've verified your account. If you change your email address, you will be asked to verify your account again.`
                                                    : 'Verify your account in order to access all of the features in your plan.'
                                            }
                                        >
                                            <TbInfoCircle
                                                style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }}
                                            />
                                        </SmallTooltip>
                                    </small>
                                </>
                            }
                        >
                            <AccountVerificationControl />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Form.Item
                            name='username'
                            style={{ marginBottom: '0.5rem' }}
                            label={
                                <>
                                    <small style={{ color: token.colorTextTertiary, marginRight: '0.375rem' }}>
                                        Username
                                        <SmallTooltip title={<>Max length of 24 characters.</>}>
                                            <TbInfoCircle
                                                style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }}
                                            />
                                        </SmallTooltip>
                                    </small>
                                </>
                            }
                            rules={[
                                { required: false, message: 'Username required' },
                                { min: 3, message: 'Must be at least 3 characters' },
                                { max: 24, message: 'Max length is 24 characters' },
                                {
                                    pattern: /^[a-z][a-z0-9_]{2,23}$/,
                                    message:
                                        'Use lowercase letters, numbers, or underscores. Must start with a letter.',
                                },
                            ]}
                        >
                            <Input
                                addonBefore={
                                    <TbAt
                                        size='1rem'
                                        style={{
                                            verticalAlign: '-0.2rem',
                                            color: token.colorTextSecondary,
                                            margin: '0 -0.25rem',
                                        }}
                                    />
                                }
                                placeholder='Username...'
                                maxLength={24}
                                onChange={(e) => {
                                    form.setFieldsValue({
                                        // IMPORTANT: This cleans the input to only allow the rules above
                                        username: normalizeUsername(e.target.value),
                                    });
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name='companyName' label='Company' style={{ marginBottom: '0.5rem' }}>
                    <CompanyDomainAutocomplete
                        placeholder='Acme Capital...'
                        selectedCompany={selectedCompanyPreview}
                        value={form.getFieldValue('companyName')}
                        onChange={(value) => {
                            form.setFieldsValue({ companyName: value });

                            if (!value || value !== companyName) {
                                form.setFieldsValue({ companyWebsite: undefined });
                            }

                            handleValuesChange();
                        }}
                        onSelect={(company) => {
                            form.setFieldsValue({ companyName: company.name, companyWebsite: company.domain });
                            handleValuesChange();
                        }}
                    />
                </Form.Item>
                <Form.Item name='title' style={{ marginBottom: '0.5rem' }} label='Title'>
                    <Input placeholder='Portfolio Manager...' />
                </Form.Item>
                <Form.Item name='bio' style={{ marginBottom: '0.5rem' }} label='Bio'>
                    <Input.TextArea
                        rows={4}
                        placeholder='About You...'
                        autoSize={false}
                        style={{ resize: 'none' }}
                        maxLength={500}
                    />
                </Form.Item>
                <Form.Item
                    name='calendarLink'
                    style={{ marginBottom: '0.5rem' }}
                    label={
                        <>
                            Calendar Link
                            <SmallTooltip
                                title={`This link is visible in your avatar dropdown as 'Book a Call with ${user?.firstName}'`}
                                placement='topLeft'
                                padding='0.25rem 0'
                            >
                                <TbInfoCircle style={{ verticalAlign: '-0.125rem', marginLeft: '0.25rem' }} />
                            </SmallTooltip>
                        </>
                    }
                >
                    <Input placeholder='https://example.com...' />
                </Form.Item>
                <Button
                    block
                    type='primary'
                    htmlType='submit'
                    loading={loading}
                    style={{ marginTop: '0.5rem' }}
                    disabled={!isDirty || isFormInvalid || loading}
                >
                    Save Changes
                </Button>
            </Form>
            <Divider style={{ margin: '1rem 0' }} />
        </>
    );
};

export default Information;
